<template>
    <Head title="Checklist Status Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.checklists.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Checklist Status Report</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Track the status and progress of checklists with details on completion, pending, and overdue inspections
                </p>
            </div>
        </div>

        <!-- Filters Section -->
        <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Filters</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Checklist Template</label>
                    <Select v-model="filters.template" :options="templateOptions" multiple placeholder="All Templates" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <Select v-model="filters.status" :options="statusOptions" multiple placeholder="All Statuses" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                    <Select v-model="filters.location" :options="locationOptions" multiple placeholder="All Locations" />
                </div>
            </div>
            <div :class="subscriptionFeatures.asset_module ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1'">
                <div v-if="subscriptionFeatures.asset_module">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Asset</label>
                    <Select v-model="filters.asset" :options="assetOptions" multiple placeholder="All Assets" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assigned To</label>
                    <Select v-model="filters.assignee" :options="assigneeOptions" multiple placeholder="All Assignees" />
                </div>
            </div>
        </div>

        <!-- Actions Toolbar -->
        <div class="flex items-center justify-between">
            <Button @click="generateReport" :disabled="loading">
                <span v-if="!loading">Generate Report</span>
                <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                </span>
            </Button>

            <div v-if="reportData.length > 0 && canExport" class="flex gap-2">
                <Button variant="secondary" @click="exportPdf">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export PDF
                </Button>
            </div>
        </div>

        <!-- Report Results -->
        <Card v-if="reportData.length > 0">
            <div class="p-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Report Results</h2>
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ reportData.length }} checklists</span>
                </div>

                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Checklist Code</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Checklist Name</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                <th v-if="subscriptionFeatures.asset_module" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Asset</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Schedule Date</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expires At</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignment Type</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assigned To</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="(row, index) in reportData" :key="index">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ row.checklist_code }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.checklist_name }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.location }}</td>
                                <td v-if="subscriptionFeatures.asset_module" class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.asset }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.schedule_date }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.due_date }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.expires_at }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.assignment_type }}</td>
                                <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{{ row.assigned_to }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span :class="getStatusClass(row.status)">{{ row.status }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>

        <!-- Empty State -->
        <Card v-else-if="!loading">
            <div class="p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select filters and click "Generate Report" to view data</p>
            </div>
        </Card>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';

const toast = useToast();
const axios = useSecureAxios();
const page = usePage();

// Subscription features
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

const props = defineProps({
    filterOptions: Object,
    canExport: Boolean,
});

// State
const loading = ref(false);
const reportData = ref([]);
const filters = ref({
    template: [],
    status: [],
    location: [],
    asset: [],
    assignee: [],
});

// Computed
const templateOptions = computed(() => {
    return props.filterOptions.templates.map(t => ({
        value: t.id,
        label: `${t.code} - ${t.name}`
    }));
});

const statusOptions = computed(() => {
    const statusLabels = {
        'assigned': 'Assigned',
        'in_progress': 'In Progress',
        'pending_approval': 'Under Review',
        'completed': 'Completed',
        'expired': 'Expired'
    };

    return props.filterOptions.statuses.map(s => ({
        value: s,
        label: statusLabels[s] || s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));
});

const locationOptions = computed(() => {
    return props.filterOptions.locations.map(l => ({ value: l, label: l }));
});

const assigneeOptions = computed(() => {
    return props.filterOptions.assignees.map(a => ({ value: a.id, label: a.name }));
});

const assetOptions = computed(() => {
    return props.filterOptions.assets.map(a => ({ value: a.id, label: a.name }));
});

// Methods
const generateReport = async () => {
    loading.value = true;

    try {
        const response = await axios.post(route('reports.checklists.checklist-status.generate'), filters.value);

        if (response.data.success) {
            reportData.value = response.data.data;
            toast.success(`Generated ${response.data.total} checklist records`);
        }
    } catch (error) {
        toast.error('Failed to generate report');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    try {
        const response = await axios.post(route('reports.checklists.export-pdf'), {
            report_type: 'checklist_status',
            data: reportData.value,
            filters: filters.value,
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `checklist-status-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        toast.error('Failed to export PDF');
        console.error(error);
    }
};

const getStatusClass = (status) => {
    const statusLower = status.toLowerCase();
    const classes = {
        'completed': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        'in progress': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'assigned': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
        'pending approval': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        'under review': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        'expired': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };

    return classes[statusLower] || 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};
</script>
